import { memo, useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { styled } from "@mui/styles";
import { Grid, TextField, InputAdornment, IconButton, Skeleton, Box } from "@mui/material";
import { Search } from "@mui/icons-material";
import CustomIcon from "../custom-icon";

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const StyledTextField = styled(TextField)(({ defaultcolor }) => ({
  backgroundColor: "#fff",
  borderRadius: "14px",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused": {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  // Label em foco
  "& .MuiInputLabel-root.Mui-focused": {
    color: `${defaultcolor} !important`,
  },
  // Label quando o campo tem valor (shrink)
  "& .MuiInputLabel-root.Mui-shrink": {
    color: `${defaultcolor} !important`,
  },
}));

const SearchContainer = styled(Box)(({ backgroundColor, isSimple }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  backgroundColor: backgroundColor || "primary",
  overflow: "hidden",
  ...(!isSimple && {
    height: "300px",
  }),
  ...(isSimple && {
    paddingBlock: "20px",
  }),
}));

const BackgroundImage = styled("img")(({ isLoaded }) => ({
  position: "absolute",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
  width: "100vw",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
  zIndex: 0,
  pointerEvents: "none",
  opacity: isLoaded ? 1 : 0,
  transition: "opacity 0.3s ease-in-out",
}));

const ImageSkeleton = styled(Skeleton)({
  position: "absolute !important",
  top: 0,
  left: 0,
  width: "100% !important",
  height: "100% !important",
  zIndex: 0,
});

// ============================================================================
// BACKGROUND IMAGE WITH SKELETON
// ============================================================================

const BackgroundImageWithSkeleton = memo(function BackgroundImageWithSkeleton({ src, clientportal }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const alt = clientportal?.mainimage?.description || "Main Background";

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoaded(true);
  }, []);

  if (!src || hasError) return null;

  return (
    <>
      {!isLoaded && (
        <ImageSkeleton
          variant="rectangular"
          animation="wave"
        />
      )}
      <BackgroundImage
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        isLoaded={isLoaded}
        onLoad={handleLoad}
        onError={handleError}
      />
    </>
  );
});

const ContentWrapper = styled(Box)({
  position: "relative",
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
});

const SearchTitle = styled("h2")(({ color }) => ({
  marginBottom: 8,
  fontWeight: "bold",
  fontSize: 24,
  color: "#fff",
  textShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
}));

// ============================================================================
// SKELETON COMPONENTS
// ============================================================================

const SimpleSearchSkeleton = memo(function SimpleSearchSkeleton() {
  return (
    <Grid item xs={12} sm={12} sx={{ paddingTop: "0 !important" }}>
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        paddingBlock: "20px"
      }}
      >
        <ImageSkeleton
          variant="rectangular"
          animation="wave"
        />

        <Skeleton
          variant="rounded"
          width="40vw"
          height={56}
          animation="wave"
          sx={{ borderRadius: "14px" }}
        />
      </Box>
    </Grid>
  );
});

const FullSearchSkeleton = memo(function FullSearchSkeleton() {
  return (
    <Grid item xs={12} sm={12} sx={{ paddingLeft: "0 !important", paddingTop: "0 !important" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          height: 300,
          gap: 1,
        }}
      >
        <ImageSkeleton
          variant="rectangular"
          animation="wave"
        />

        <Skeleton variant="text" width={200} height={36} animation="wave" />
        <Skeleton
          variant="rounded"
          width={"40vw"}
          height={56}
          animation="wave"
          sx={{ borderRadius: "14px", maxWidth: "60vw" }}
        />
      </Box>
    </Grid>
  );
});

// ============================================================================
// SEARCH INPUT COMPONENT
// ============================================================================

const SearchInput = memo(function SearchInput({
  name,
  value,
  label,
  placeholder,
  onChange,
  onSearch,
  defaultcolor
}) {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onSearch?.(value);
      }
    },
    [onSearch, value]
  );

  const handleSearchClick = useCallback(() => {
    onSearch?.(value);
  }, [onSearch, value]);

  const inputProps = useMemo(
    () => ({
      endAdornment: (
        <InputAdornment position="end">
          <IconButton onClick={handleSearchClick}>
            <CustomIcon icon={Search} color={defaultcolor} />
          </IconButton>
        </InputAdornment>
      ),
    }),
    [handleSearchClick, defaultcolor]
  );

  return (
    <StyledTextField
      variant="outlined"
      name={name}
      label={label}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      onKeyDown={handleKeyDown}
      sx={{ width: "40vw" }}
      InputProps={inputProps}
      defaultcolor={defaultcolor}
    />
  );
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function CommonSearchExternal({
  state,
  clientportal,
  simple = false,
  loading = false,
  nameTextField = "searchvalue",
  mainImageSrc,
  handleChange,
  searchArticle,
}) {
  const intl = useIntl();

  const searchValue = state?.[nameTextField] ?? "";

  const defaulttextcolor = clientportal?.defaulttextcolor || "primary";
  const defaultcolor = clientportal?.defaultcolor || "primary";

  const searchLabel = useMemo(
    () => intl.formatMessage({ id: "search" }),
    [intl]
  );

  const searchPlaceholder = useMemo(
    () => intl.formatMessage({ id: "search.news.placeholder" }),
    [intl]
  );

  const handleSearch = useCallback(
    (value) => {
      if (value?.trim()) {
        searchArticle?.(value);
      }
    },
    [searchArticle]
  );

  // Loading skeleton states
  if (loading) {
    return simple ? <SimpleSearchSkeleton /> : <FullSearchSkeleton />;
  }

  // Simple search variant (compact header)
  if (simple) {
    return (
      <Grid item xs={12} sm={12} sx={{ paddingTop: "0 !important" }}>
        <SearchContainer
          id="mainimage"
          backgroundColor={defaultcolor}
          isSimple
        >
          <ContentWrapper>
            {clientportal.enablesearch && (
              <SearchInput
                name={nameTextField}
                value={searchValue}
                label={searchLabel}
                onChange={handleChange}
                onSearch={handleSearch}
                defaultcolor={defaultcolor}
              />
            )}
          </ContentWrapper>
        </SearchContainer>
      </Grid>
    );
  }

  // Full search variant (with optional background image)
  return (
    <Grid item xs={12} sm={12} sx={{ paddingLeft: "0 !important", paddingTop: "0 !important" }}>
      <SearchContainer
        id="mainimage"
        backgroundColor={defaultcolor}
        isSimple={false}
      >
        <BackgroundImageWithSkeleton src={mainImageSrc} clientportal={clientportal} />
        <ContentWrapper>
          {clientportal.searchtext && (
            <SearchTitle color={defaulttextcolor}>{clientportal.searchtext}</SearchTitle>
          )}
          {clientportal.enablesearch && (
            <SearchInput
              name={nameTextField}
              value={searchValue}
              label={searchLabel}
              placeholder={searchPlaceholder}
              onChange={handleChange}
              onSearch={handleSearch}
              defaultcolor={defaultcolor}
            />
          )}
        </ContentWrapper>
      </SearchContainer>
    </Grid>
  );
}

export default memo(CommonSearchExternal);